import React, { useState, useMemo } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { FileText, Edit2, Trash2, Search, Copy, Send, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../api";
import { useAuth } from "../Auth/AuthContext";
import { isOrgAdmin } from "../../utils/roles";

const PAGE_SIZE = 20;

const STATUS_CONFIG = {
  pending:  { label: "invoice.status_pending",  cls: "bg-amber-500/10 text-amber-500" },
  paid:     { label: "invoice.status_paid",     cls: "bg-emerald-500/10 text-emerald-500" },
  canceled: { label: "invoice.status_canceled", cls: "bg-rose-500/10 text-rose-500" },
};

const InvoiceList = ({ invoices, onEdit, refresh }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = isOrgAdmin(user);
  const [search, setSearch]             = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [dateFilter, setDateFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage]                 = useState(1);
  const [confirmId, setConfirmId]       = useState(null);
  const [sendingId, setSendingId]       = useState(null);
  const [qontoConfirm, setQontoConfirm] = useState(null);

  const clients = useMemo(() => [...new Set(invoices.map(i => i.client_name))], [invoices]);

  const filtered = useMemo(() => {
    return invoices.filter(inv => {
      const q = search.toLowerCase();
      const searchMatch = inv.client_name?.toLowerCase().includes(q)
        || inv.id?.toString().includes(q)
        || inv.client_email?.toLowerCase().includes(q);
      const clientMatch = clientFilter ? inv.client_name === clientFilter : true;
      const dateMatch   = dateFilter
        ? dayjs(inv.date).format("YYYY-MM-DD").startsWith(dateFilter)
        : true;
      const statusMatch = statusFilter ? inv.status === statusFilter : true;
      return searchMatch && clientMatch && dateMatch && statusMatch;
    });
  }, [invoices, search, clientFilter, dateFilter, statusFilter]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  React.useEffect(() => { setPage(1); }, [search, clientFilter, dateFilter, statusFilter]);

  const handleDelete = (id) => setConfirmId(id);

  const confirmDelete = async () => {
    const id = confirmId;
    setConfirmId(null);
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("invoice.deleteSuccess", "Facture supprimée"));
      refresh();
    } catch {
      toast.error(t("invoice.deleteError", "Erreur lors de la suppression"));
    }
  };

  const handleDuplicate = async (inv) => {
    try {
      await api.post("/invoices", {
        client_name:    inv.client_name,
        client_address: inv.client_address,
        client_city:    inv.client_city,
        client_phone:   inv.client_phone,
        client_email:   inv.client_email,
        client_siret:   inv.client_siret || null,
        date:           new Date().toISOString().slice(0, 10),
        prestations:    inv.prestations,
        totalHT:        inv.totalHT,
        totalTVA:       inv.totalTVA,
        totalTTC:       inv.totalTTC,
        status:         "pending",
      });
      toast.success(t("invoice.copySuccess", "✅ Facture dupliquée"));
      refresh();
    } catch {
      toast.error(t("invoice.copyError", "❌ Erreur lors de la duplication"));
    }
  };

  const handleViewPDF = async (id) => {
    try {
      const r = await api.get(`/invoices/generate/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch {
      alert(t("invoice.pdfError") || "Erreur lors de l'ouverture du PDF");
    }
  };

  const confirmSendToQonto = async () => {
    const inv = qontoConfirm;
    setQontoConfirm(null);
    if (!inv) return;
    setSendingId(inv.id);
    try {
      await api.post(`/invoices/${inv.id}/qonto-send`);
      toast.success(t("invoice.qontoSendSuccess", "✅ Facture envoyée vers Qonto"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || t("invoice.qontoSendError", "❌ Erreur lors de l'envoi vers Qonto"));
    } finally {
      setSendingId(null);
    }
  };

  const handleViewQontoPDF = async (id) => {
    try {
      const r = await api.get(`/invoices/${id}/qonto-pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch {
      toast.error(t("invoice.qontoPdfError", "❌ Erreur lors de l'ouverture du PDF Qonto"));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await api.put(`/invoices/${id}/status`, { status: newStatus });
      refresh();
    } catch { alert(t("invoice.statusUpdateError")); }
  };

  const hasFilters = search || clientFilter || dateFilter || statusFilter;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-base-100 rounded-xl border border-base-200 shadow-sm p-4 space-y-3">
        {/* Ligne 1 : Statuts en pills */}
        <div className="flex flex-wrap gap-2">
          {[
            { value: "",         label: "Toutes",     cls: "bg-base-200 text-base-content/70 border-base-300 hover:bg-base-300" },
            { value: "pending",  label: t("invoice.status_pending",  "En attente"), cls: "bg-amber-500/10 text-amber-600 border-amber-500/30 hover:bg-amber-500/20" },
            { value: "paid",     label: t("invoice.status_paid",     "Payée"),      cls: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 hover:bg-emerald-500/20" },
            { value: "canceled", label: t("invoice.status_canceled", "Annulée"),    cls: "bg-rose-500/10 text-rose-600 border-rose-500/30 hover:bg-rose-500/20" },
          ].map(({ value, label, cls }) => (
            <button
              key={value}
              onClick={() => setStatusFilter(value)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${cls} ${
                statusFilter === value ? "ring-2 ring-offset-1 ring-current" : ""
              }`}
            >
              {label}
              {value && (
                <span className="ml-1 opacity-60">
                  ({invoices.filter((i) => i.status === value).length})
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Ligne 2 : Recherche + client + mois */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 pointer-events-none" />
            <input
              type="text"
              placeholder={t("invoice.searchPlaceholder")}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input input-bordered input-sm pl-8 bg-base-200 border-base-300 focus:border-primary focus:outline-none text-base-content placeholder:text-base-content/40 min-w-[180px]"
            />
          </div>
          <select
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            className="select select-bordered select-sm bg-base-200 border-base-300 text-base-content/70 focus:outline-none"
          >
            <option value="">{t("invoice.allClients")}</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="month"
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="input input-bordered input-sm bg-base-200 border-base-300 text-base-content/70 focus:border-primary focus:outline-none"
          />
          {hasFilters && (
            <button
              className="text-xs text-base-content/40 hover:text-base-content/70 underline transition-colors"
              onClick={() => { setSearch(""); setClientFilter(""); setDateFilter(""); setStatusFilter(""); }}
            >
              Réinitialiser
            </button>
          )}
          <span className="ml-auto text-xs text-base-content/40 font-medium">
            {filtered.length} facture{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100">
        <table className="table table-sm">
          <thead>
            <tr className="bg-base-200 border-b border-base-200">
              <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-3">
                {t("invoice.number")}
              </th>
              <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-3">
                {t("invoice.date")}
              </th>
              <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-3">
                {t("invoice.clientName")}
              </th>
              <th className="text-right text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-3">
                {t("invoice.totalTTC")}
              </th>
              <th className="text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-3">
                {t("invoice.status")}
              </th>
              <th className="text-center text-[11px] font-bold tracking-widest text-base-content/50 uppercase py-3">
                {t("invoice.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-base-content/40">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-base-200 flex items-center justify-center">
                      <FileText size={22} className="text-base-content/30" />
                    </div>
                    <span className="text-sm">{t("invoice.none")}</span>
                  </div>
                </td>
              </tr>
            ) : paginated.map(f => {
              const cfg = STATUS_CONFIG[f.status] || { label: f.status, cls: "bg-base-200 text-base-content/70" };
              return (
                <tr key={f.id} className="hover:bg-base-200/50 transition-colors group">
                  <td className="py-3 font-mono text-xs text-base-content/40">#{f.id}</td>
                  <td className="py-3 text-sm text-base-content/70 whitespace-nowrap">
                    {dayjs(f.date).format("DD/MM/YYYY")}
                  </td>
                  <td className="py-3 text-sm font-semibold text-base-content">{f.client_name}</td>
                  <td className="py-3 text-right font-mono font-bold text-base-content text-sm">
                    {Number(f.totalTTC || 0).toLocaleString("fr-FR")} €
                  </td>
                  <td className="py-3">
                    <select
                      value={f.status}
                      onChange={e => handleStatusChange(f.id, e.target.value)}
                      className={`text-xs font-semibold px-2.5 py-1 rounded-lg cursor-pointer outline-none appearance-none ${cfg.cls}`}
                    >
                      {Object.entries(STATUS_CONFIG).map(([val, { label }]) => (
                        <option key={val} value={val}>{t(label)}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <InvActionBtn onClick={() => handleViewPDF(f.id)} title="PDF">
                        <FileText size={13} />
                      </InvActionBtn>
                      <InvActionBtn onClick={() => onEdit(f)} title={t("invoice.edit")}>
                        <Edit2 size={13} />
                      </InvActionBtn>
                      <InvActionBtn onClick={() => handleDuplicate(f)} title={t("invoice.duplicate", "Dupliquer")}>
                        <Copy size={13} />
                      </InvActionBtn>
                      {isAdmin && (
                        f.qonto_invoice_id ? (
                          <InvActionBtn
                            onClick={() => handleViewQontoPDF(f.id)}
                            title={t("invoice.qontoPdf", "PDF Qonto (Factur-X)")}
                          >
                            <CheckCircle2 size={13} className="text-emerald-500" />
                          </InvActionBtn>
                        ) : (
                          <InvActionBtn
                            onClick={() => setQontoConfirm(f)}
                            title={t("invoice.qontoSend", "Envoyer vers Qonto")}
                            disabled={sendingId === f.id}
                          >
                            <Send size={13} />
                          </InvActionBtn>
                        )
                      )}
                      <InvActionBtn onClick={() => handleDelete(f.id)} title={t("invoice.delete")} danger>
                        <Trash2 size={13} />
                      </InvActionBtn>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Confirm Delete Modal */}
      {confirmId && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-sm w-full animate-fade-in">
            <h3 className="font-bold text-base-content text-base mb-1">{t("invoice.confirmDelete")}</h3>
            <p className="text-sm text-base-content/50 mb-5">{t("invoice.delete")}</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmId(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button onClick={confirmDelete} className="btn btn-error btn-sm">{t("buttons.delete")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Qonto Send Modal — action irréversible (entre en comptabilité) */}
      {qontoConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-xl border border-base-200 shadow-2xl p-6 max-w-md w-full animate-fade-in">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0">
                <Send size={18} className="text-amber-500" />
              </div>
              <div>
                <h3 className="font-bold text-base-content text-base">{t("invoice.qontoConfirmTitle", "Émettre la facture sur Qonto ?")}</h3>
                <p className="text-sm text-base-content/60 mt-1">
                  {t("invoice.qontoConfirmBody", "Cette facture sera émise officiellement et entrera dans votre comptabilité. Cette action est définitive et irréversible.")}
                </p>
              </div>
            </div>
            <div className="bg-base-200/60 rounded-lg px-4 py-3 mb-5 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-base-content/50">{t("invoice.clientName")}</span><span className="font-semibold text-base-content">{qontoConfirm.client_name}</span></div>
              <div className="flex justify-between"><span className="text-base-content/50">{t("invoice.totalTTC")}</span><span className="font-mono font-bold text-base-content">{Number(qontoConfirm.totalTTC || 0).toLocaleString("fr-FR")} €</span></div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setQontoConfirm(null)} className="btn btn-ghost btn-sm">{t("buttons.cancel")}</button>
              <button onClick={confirmSendToQonto} className="btn btn-warning btn-sm gap-1.5">
                <Send size={13} />
                {t("invoice.qontoConfirmAction", "Émettre définitivement")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-xs text-base-content/40">
            Page {page} sur {pageCount}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="btn btn-ghost btn-xs border border-base-300 text-base-content/70 disabled:opacity-40"
            >
              ← Préc.
            </button>
            {Array.from({ length: Math.min(pageCount, 5) }, (_, i) => {
              const pg = i + 1;
              return (
                <button
                  key={pg}
                  onClick={() => setPage(pg)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${
                    page === pg
                      ? "bg-primary text-primary-content border border-primary"
                      : "border border-base-300 bg-base-100 text-base-content/70 hover:bg-base-200"
                  }`}
                >
                  {pg}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(pageCount, p + 1))}
              disabled={page === pageCount}
              className="btn btn-ghost btn-xs border border-base-300 text-base-content/70 disabled:opacity-40"
            >
              Suiv. →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceList;

function InvActionBtn({ children, onClick, title, danger = false, disabled = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      disabled={disabled}
      className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
        danger
          ? "text-base-content/40 hover:text-rose-500 hover:bg-rose-500/10"
          : "text-base-content/40 hover:text-base-content hover:bg-base-200"
      }`}
    >
      {children}
    </button>
  );
}
